import { useState, useEffect } from 'react';
import { Server } from '@wajeez/wajeez.microservices';
import {
  Control,
  FieldArrayWithId,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import {
  FormInputController,
  FormSwitchInputV2,
  FormTextInputV2,
} from '@wajeez-web/components/form-builder';
import { Autocomplete, Select } from '@wajeez-web/components/mui';
import {
  ButtonHeadless,
  InputLabel,
  LanguagesLayout,
} from '@wajeez-web/components/ui';
import { useTranslation } from 'react-i18next';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import { AiFillDelete } from 'react-icons/ai';
import { BiLoaderAlt } from 'react-icons/bi';
import {
  ComponentTypesActions,
  createComponentTypeMultilingual,
  editComponentTypeMultilingual,
  useContentDispatch,
  useContentSelector,
} from '@wajeez-web/stores/content';

import { ComponentTypeFieldOptionsComponents } from './ComponentTypeFieldOptionsComponents';
import { ConfirmationModal } from './ConfirmationModal';
import { Languages, enumMapperValueAsNumber } from '@wajeez-web/core/utils';

export const fieldTypes: { id: Server.FieldType; name: string }[] = [
  { id: Server.FieldType.Text, name: 'Text' },
  { id: Server.FieldType.AreaText, name: 'AreaText' },
  { id: Server.FieldType.TineMCE, name: 'TineMCE' },
  { id: Server.FieldType.DateTime, name: 'DateTime' },
  { id: Server.FieldType.Date, name: 'Date' },
  { id: Server.FieldType.DragFile, name: 'DragFile' },
  { id: Server.FieldType.UploadFile, name: 'UploadFile' },
  { id: Server.FieldType.UploadImage, name: 'UploadImage' },
  { id: Server.FieldType.CallbackSelect, name: 'CallbackSelect' },
  { id: Server.FieldType.Select, name: 'Select' },
  { id: Server.FieldType.Number, name: 'Number' },
  { id: Server.FieldType.RangePicker, name: 'RangePicker' },
  { id: Server.FieldType.ColorPicker, name: 'ColorPicker' },
  { id: Server.FieldType.Switch, name: 'Switch' },
  { id: Server.FieldType.RequiredComponent, name: 'RequiredComponent' },
  { id: Server.FieldType.MappedField, name: 'MappedField ' },
  { id: Server.FieldType.PredefinedComponent, name: 'PredefinedComponent ' },
];

interface FieldsType extends Server.CreateComponentTypeFieldV2Input {
  id: number;
}
export interface FormInput extends Server.CreateComponentTypeV2Input {
  id?: number;
  fields: FieldsType[];
}

interface ComponentTypeFormBuilderProps {
  type: 'Create' | 'Edit';
  onSubmit: (componentTypeId: number) => void;
  onCancel: () => void;
  data?: Server.ComponentTypeMultilingualOutput | undefined;
}

const usageTypes = enumMapperValueAsNumber(Server.ComponentUsageType);

export const ComponentTypeFormBuilder = ({
  type,
  onSubmit,
  onCancel,
  data,
}: ComponentTypeFormBuilderProps) => {
  const id = data?.id;

  const [isCancelConfirmationModalOpen, setIsCancelConfirmationModalOpen] =
    useState(false);

  const createComponentTypeMultilingualState = useContentSelector(
    ({ componentTypes }) => componentTypes.createComponentType
  );

  const editComponentTypeMultilingualState = useContentSelector(
    ({ componentTypes }) => componentTypes.editComponentTypeMultilingual
  );

  const addAllowedTypeStatus = useContentSelector(
    ({ collectionComponentTypes }) =>
      collectionComponentTypes.addAllowedType.status
  );

  const componentTypesLookups = useContentSelector(
    ({ componentTypes }) => componentTypes.componentTypesLookups
  );

  const submitStatus =
    createComponentTypeMultilingualState.status === 'loading' ||
    editComponentTypeMultilingualState.status === 'loading' ||
    addAllowedTypeStatus === 'loading';

  const {
    control,
    handleSubmit,
    watch,
    formState: { isDirty, dirtyFields },
  } = useForm<FormInput>({
    defaultValues: data || { profiles: [{ languageId: Languages.AR }] },
  });

  // watch((data) => {
  //   console.log(data);
  // });

  const {
    fields: profilesMain,
    append: appendProfileMain,
    remove: removeProfileMain,
  } = useFieldArray({
    control,
    name: 'profiles',
  });

  const {
    fields,
    prepend: prependField,
    remove: removeField,
  } = useFieldArray({
    control,
    name: 'fields',
  });

  const { t } = useTranslation();

  const onFormSubmit: SubmitHandler<FormInput> = async (data) => {
    const mappedData: FormInput = {
      ...data,
      contentItemType: Number(data.contentItemType),
      usageType: Number(data.usageType),
    };

    if (type === 'Create') {
      createComponentTypeMultilingual(mappedData, onSubmit);
    } else {
      editComponentTypeMultilingual(
        {
          ...mappedData,
          id,
          allowedItemTemplateTypes: data.allowedItemTemplateTypes,
        },
        onSubmit
      );
    }
  };

  const renderFields = () => {
    return fields.map((f, i) => {
      return (
        <div key={f.id}>
          <ComponentTypeField
            control={control}
            fieldIndex={i}
            field={f}
            onFieldRemove={() => {
              removeField(i);
            }}
            formType={type}
          />
        </div>
      );
    });
  };

  const renderProfileMain = (language: Languages) => {
    const selectedProfileIndexMain = profilesMain.findIndex(
      (p) => p.languageId === language
    );

    return (
      <div
        key={`profiles.${selectedProfileIndexMain}`}
        className="w-[400px] flex flex-col space-y-2"
      >
        <FormTextInputV2
          control={control}
          name={`profiles.${selectedProfileIndexMain}.title`}
          fieldMeta={{
            fieldType: Server.FieldType.Text,
            required: true,
            label: 'Title',
            placeHolder: 'Title',
            fieldOptions: {},
          }}
        />
        <FormTextInputV2
          control={control}
          name={`profiles.${selectedProfileIndexMain}.description`}
          fieldMeta={{
            fieldType: Server.FieldType.Text,
            required: true,
            label: 'Description',
            placeHolder: 'Description',
            fieldOptions: {},
          }}
        />
      </div>
    );
  };

  return (
    <div className="w-full">
      <form
        onSubmit={handleSubmit(onFormSubmit)}
        className="w-full flex flex-col justify-start items-start"
        noValidate
      >
        <div className="w-full pb-6 border-b-[2px]">
          <FormSectionTitle sectionNumber={1} title="Basic Information" />
          <div className="w-full">
            <div className="w-[400px] flex flex-col justify-start items-start space-y-2">
              <FormTextInputV2
                control={control}
                name="name"
                fieldMeta={{
                  fieldType: Server.FieldType.Text,
                  required: true,
                  label: 'Name',
                  placeHolder: 'Name',
                  fieldOptions: {},
                }}
                disabled={type === 'Edit'}
              />
              <FormInputController
                control={control}
                name={`contentItemType`}
                fieldMeta={{
                  fieldType: Server.FieldType.Select,
                  fieldOptions: {},
                  required: true,
                }}
                renderInput={({ ...props }) => (
                  <div className="w-full">
                    <Autocomplete
                      id={`contentItemType-autocomplete`}
                      options={[
                        { value: 0, label: 'Component' },
                        { value: 1, label: 'Collection Component' },
                      ]}
                      label="Content Type"
                      color="#1f7983"
                      heightClassName="h-full"
                      required
                      {...props}
                    />
                  </div>
                )}
              />
              <FormInputController
                control={control}
                name={`usageType`}
                fieldMeta={{
                  fieldType: Server.FieldType.Select,
                  fieldOptions: {},
                  required: true,
                }}
                renderInput={({ ...props }) => (
                  <div className="w-full">
                    <Autocomplete
                      id={`usageType-autocomplete`}
                      options={usageTypes}
                      label="Usage Type"
                      color="#1f7983"
                      heightClassName="h-full"
                      required
                      {...props}
                    />
                  </div>
                )}
              />
              <div className="w-full">
                <FormInputController
                  control={control}
                  name={`allowedItemTemplateTypes`}
                  fieldMeta={{
                    fieldType: Server.FieldType.Select,
                    fieldOptions: {},
                  }}
                  renderInput={({ ...props }) => (
                    <Autocomplete
                      id={`allowedItemTemplateTypes`}
                      options={componentTypesLookups.data.map((t) => {
                        return {
                          value: t.id,
                          label: t.name,
                        };
                      })}
                      label="Allowed Item Template Types"
                      color="#1f7983"
                      multiple
                      heightClassName="h-auto"
                      {...props}
                    />
                  )}
                />
              </div>
            </div>
            <div className="mt-5">
              <LanguagesLayout
                renderPage={renderProfileMain}
                defaultLanguages={profilesMain.map((s) => s.languageId)}
                onAdd={function (language: Languages): void {
                  appendProfileMain({
                    languageId: language,
                    title: '',
                    description: '',
                  });
                }}
                onDelete={function (language: Languages): void {
                  const selectedProfileIndexMain = profilesMain.findIndex(
                    (p) => p.languageId === language
                  );

                  removeProfileMain(selectedProfileIndexMain);
                }}
              />
            </div>
          </div>
        </div>
        <div className="mt-6">
          <FormSectionTitle sectionNumber={2} title="Dynamic Fields" />
          <div className="w-[150px]">
            <InputLabel
              text={
                t(`inputs.labels.${'Add Field'}`, 'Add Field' || '') as string
              }
              name={'fieldType' as string}
            />
            <div className="h-[30px]">
              <Select
                name="fieldType"
                options={fieldTypes.map((f) => ({
                  value: f.id.toString(),
                  label: f.name,
                }))}
                onChange={(option) => {
                  const value = Number(option.value) as Server.FieldType;

                  prependField({
                    id: Date.now(),
                    fieldType: value,
                    name: '',
                    fieldOptions: {},
                    required: false,
                    profiles: [
                      {
                        languageId: Languages.AR,
                        label: '',
                        placeHolder: '',
                        span: '',
                      },
                    ],
                  });
                }}
                option={{ value: '', label: '' }}
                disabled={type === 'Edit'}
              />
            </div>
          </div>
          <div className="mt-3 w-full">{renderFields()}</div>
        </div>
        <div className="absolute top-[-40px] rtl:left-0 ltr:right-0 w-full flex justify-end items-center space-s-2">
          <div className="mt-10 w-[70px]">
            <ButtonHeadless
              bgStyle="bg-warmGray-300"
              paddingYStyle="py-1"
              borderRadiusStyle="rounded-[6px]"
              action={() => {
                if (isDirty) {
                  setIsCancelConfirmationModalOpen(true);
                } else {
                  onCancel && onCancel();
                }
              }}
            >
              <div className="text-warmGray-400 text-[16px] font-bold">
                <ReturnTranslatedText
                  origintext="Cancel"
                  text={`buttons.${'Cancel'}`}
                />
              </div>
            </ButtonHeadless>
          </div>
          <div className="mt-10 w-[70px]">
            <ButtonHeadless
              type="submit"
              paddingYStyle="py-1"
              borderRadiusStyle="rounded-[6px]"
              isDisabled={submitStatus}
            >
              <div className="flex justify-center items-center text-white text-[16px] font-bold">
                <ReturnTranslatedText
                  origintext="Save"
                  text={`buttons.${'Save'}`}
                />
                {submitStatus && (
                  <BiLoaderAlt className="mr-1 animate-spin w-5" />
                )}
              </div>
            </ButtonHeadless>
          </div>
        </div>
      </form>
      {isCancelConfirmationModalOpen && (
        <ConfirmationModal
          isOpen={isCancelConfirmationModalOpen}
          onClose={() => {
            setIsCancelConfirmationModalOpen(false);
          }}
          onSubmit={() => {
            onCancel && onCancel();
          }}
          message="You didn't save the changes, are you sure that you want to cancel?"
        />
      )}
    </div>
  );
};

interface ComponentTypeFieldProps {
  control: Control<FormInput, any>;
  fieldIndex: number;
  field: FieldArrayWithId<FormInput, 'fields', 'id'>;
  onFieldRemove: () => void;
  formType: 'Create' | 'Edit';
}

const ComponentTypeField = ({
  control,
  fieldIndex,
  field,
  onFieldRemove,
  formType,
}: ComponentTypeFieldProps) => {
  const {
    fields: fieldProfiles,
    append: appendFieldProfile,
    remove: removeFieldProfile,
  } = useFieldArray({
    control,
    name: `fields.${fieldIndex}.profiles`,
  });

  const renderFieldProfile = (language: Languages) => {
    const selectedFieldProfileIndex = fieldProfiles.findIndex(
      (p) => p.languageId === language
    );

    return (
      <div
        key={`fields.${fieldIndex}.profiles.${selectedFieldProfileIndex}`}
        className="w-full flex flex-col space-y-2"
      >
        <FormTextInputV2
          control={control}
          name={`fields.${fieldIndex}.profiles.${selectedFieldProfileIndex}.label`}
          fieldMeta={{
            fieldType: Server.FieldType.Text,
            required: true,
            label: 'Label',
            placeHolder: 'Label',
            fieldOptions: {},
          }}
        />
        <FormTextInputV2
          control={control}
          name={`fields.${fieldIndex}.profiles.${selectedFieldProfileIndex}.placeHolder`}
          fieldMeta={{
            fieldType: Server.FieldType.Text,
            required: true,
            label: 'Placeholder',
            placeHolder: 'Placeholder',
            fieldOptions: {},
          }}
        />
      </div>
    );
  };

  return (
    <div className="mt-3 w-[650px] p-4 flex justify-between items-center border rounded">
      <div className="w-full">
        <div className="mb-3 text-[17px] font-bold">
          {fieldTypes.find((t) => t.id === field.fieldType)?.name}
        </div>
        <div className="w-full grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <FormSwitchInputV2
              control={control}
              name={`fields.${fieldIndex}.required`}
              fieldMeta={{
                fieldType: Server.FieldType.Switch,
                label: 'Required?',
                fieldOptions: {},
              }}
            />
          </div>
          <FormTextInputV2
            control={control}
            name={`fields.${fieldIndex}.name`}
            fieldMeta={{
              fieldType: Server.FieldType.Text,
              required: true,
              label: 'Name',
              placeHolder: 'Name',
              fieldOptions: {},
            }}
            disabled={formType === 'Edit'}
          />
          <div className="col-span-2">
            <LanguagesLayout
              renderPage={renderFieldProfile}
              defaultLanguages={fieldProfiles.map((s) => s.languageId)}
              onAdd={function (language: Languages): void {
                appendFieldProfile({
                  languageId: language,
                  label: '',
                  placeHolder: '',
                  span: '',
                });
              }}
              onDelete={function (language: Languages): void {
                const selectedProfileIndexMain = fieldProfiles.findIndex(
                  (p) => p.languageId === language
                );

                removeFieldProfile(selectedProfileIndexMain);
              }}
            />
          </div>
          <ComponentTypeFieldOptionsComponents
            fieldType={field.fieldType}
            fieldIndex={fieldIndex}
            name={`fields.${fieldIndex}.fieldOptions`}
            control={control}
          />
        </div>
      </div>
      {formType !== 'Edit' && (
        <div
          className="mr-[30px] text-red-500 cursor-pointer hover:opacity-75 active:opacity-100"
          onClick={onFieldRemove}
        >
          <AiFillDelete />
        </div>
      )}
    </div>
  );
};

interface FormSectionTitleProps {
  sectionNumber: number;
  title: string;
}

const FormSectionTitle = ({ sectionNumber, title }: FormSectionTitleProps) => {
  return (
    <div className="mb-4 w-full flex justify-start items-center">
      <div className="w-5 h-5 flex justify-center items-center bg-[#212529] rounded-full text-white text-[15px] font-medium">
        {sectionNumber}
      </div>
      <div className="rtl:mr-2 ltr:ml-2 text-[19px] font-medium">
        <ReturnTranslatedText text={`general.${title}`} origintext={title} />
      </div>
    </div>
  );
};
